import * as React from 'react';
import styled from 'styled-components';
import { ExternalSecondaryLinkButton, FlexSm } from 'components/Elements';
import { theme } from 'theme';
import strings from 'strings/strings.json';
import Button from 'components/buttons/Button';
import ExternalLinkIcon from 'assets/icons/navigation/ExternalLinkIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { useElementWidth } from 'hooks/useElementWidth';
import { ResourcesInput } from './activityForms/shared/SharedActivityFormComponents';

type ResourceCardProps = {
    index: number;
    resource: ResourcesInput;
    noUniqueId?: boolean;
    deleteResource: (index: number) => void;
    disabled: boolean;
};

export default function ResourceCard({
    index,
    resource,
    noUniqueId = false,
    deleteResource,
    disabled,
}: ResourceCardProps): JSX.Element {
    const [ref, width] = useElementWidth<HTMLDivElement>();

    if (width === undefined) {
        return <Container key={index} ref={ref} $break={false} />;
    }

    function mouseDownHandler(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
        if (noUniqueId) return;
        // this will track 2 ways of opening the link
        // left click (button 0) and middle click (button 1)
        // using a simple onClick would only track left clicks
        if (event.button !== 0 && event.button !== 1) return;
    }

    const breakpoint = width <= 350;

    return (
        <Container key={index} ref={ref} $break={breakpoint}>
            <LinkContainer>
                <IconContainer>
                    <ExternalLinkIcon size={IconSize.LARGE} colour={theme.primaryBrandColour} />
                </IconContainer>
                <ExternalSecondaryLinkButton
                    id={`resourceLink-${index}`}
                    href={resource.url}
                    target={'_blank'}
                    aria-label={strings.general.openInNewTab}
                    onMouseDown={(event: any) => mouseDownHandler(event)}
                >
                    {resource.title}
                </ExternalSecondaryLinkButton>
            </LinkContainer>
            <Button
                title={strings.resourceCard.removeResourcesButton}
                aria={strings.resourceCard.removeResourcesButtonAria}
                colour={'BLACK'}
                onClick={() => deleteResource(index)}
                disabled={disabled}
                fullwidth={breakpoint}
            />
        </Container>
    );
}

const IconContainer = styled.div`
    display: flex;
    align-items: center;
`;

const Container = styled(FlexSm)<{ $break: boolean }>`
    align-items: center;
    justify-content: space-between;
    flex-direction: ${({ $break }) => ($break ? 'column' : 'row')};
`;

const LinkContainer = styled(FlexSm)`
    align-items: center;
    width: 100%;
`;
